/**
 * LocalMediaSheet
 *
 * Dismissible viewer that renders a **locally-picked** feedback screenshot (a
 * `Blob`/`File`, not a remote URL) full-size via `ContentThumbnail`.
 *
 * Web replacement for the destination of Flutter's `_onScreenshotTapped`
 * (`lib/presentation/screens/feedback/in_app_feedback_screen.dart:83-111`),
 * which builds a local `UserMedia` and pushes the content-list screen as a
 * plain centered local-image viewer (`hasReactions:false`, `showMediaInfo:false`,
 * `displayAddComment:false`, `centerContent:true`). On the web the picked
 * screenshot is opened here instead.
 *
 * Fire-and-forget: `show` returns nothing (no result needed). The object URL
 * built from the file is revoked when the sheet content unmounts (sheet close),
 * avoiding a leak.
 */

import { SheetService } from '@/presentation/components/common/sheet/service/SheetService.index';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';
import { LocalMediaSheetContent } from './components/LocalMediaSheetContent';

/**
 * Configuration for showing the local-media viewer sheet.
 */
export interface LocalMediaSheetShowOptions {
  /** The locally-picked screenshot to display (an object URL is built from it). */
  file: Blob;
  /** Screenshot aspect ratio (width / height) used to size the box. */
  aspectRatio: number;
  /**
   * Header title rendered in the sheet's native `title` slot (above the image).
   * Resolved at the caller because `show` is a plain module function with no
   * hook access to `l10n`.
   */
  title: string;
  /**
   * Label for the Close button rendered in the sheet's native `cta` slot (below
   * the image). Resolved at the caller for the same reason as `title`.
   */
  closeLabel: string;
  /** Optional screen key of the route opening this sheet. */
  screenKey?: string;
  /** Optional fixed height for the sheet surface (e.g. `'85dvh'`). */
  fixedHeight?: string;
}

/**
 * Show the local-media viewer sheet (fire-and-forget, dismissible).
 */
const show = ({
  file,
  aspectRatio,
  title,
  closeLabel,
  screenKey,
  fixedHeight,
}: LocalMediaSheetShowOptions) => {
  // A missing/invalid ratio falls back to the same default the feedback grid
  // uses, mirroring `DefaultMediaPage`.
  const safeAspectRatio =
    Number.isFinite(aspectRatio) && aspectRatio > 0
      ? aspectRatio
      : VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO;

  SheetService.show({
    content: (
      <LocalMediaSheetContent file={file} aspectRatio={safeAspectRatio} />
    ),
    // Native sheet header — renders above the content in both BottomSheetView
    // and DialogView.
    title,
    // Native sheet footer — CTAButtons pins it below the content and
    // auto-closes on click (closeOnClick defaults to true), so the handler is a
    // no-op. `qaId` preserves the pre-refactor `local-media-close` id.
    cta: {
      buttons: [
        {
          text: closeLabel,
          onClick: () => {},
          qaId: 'local-media-close',
        },
      ],
    },
    screenKey,
    closeOnClickOutside: true,
    fixedHeight,
    qaId: 'local-media',
  });
};

export const LocalMediaSheet = {
  show,
};
